import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  FunctionComponent,
} from "react";

import { useNavigate, useMatch } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Device, Call } from "@twilio/voice-sdk";

import axios from "axios";

import Logger from "../../lib/logger";

import { client } from "../../bit/board/hooks/useGraphQLClient";
import { useReceptionistPhone } from "../../lib/hooks/useQueryBoard";

import CallingModal from "./CallingModal";

import { useOfflineContext } from "../../context/Offline";
import { QUERY_ACCESS_EVENT } from "../../config/graphql/query";

const isDev = process.env.NODE_ENV === "development";

const CLIENT_TOKEN = isDev
  ? process.env.API_CLIENT_TOKEN
  : window.location.pathname.split("/")[2];

const api = axios.create({
  baseURL: `${process.env.API_URL}/api/v2`,
  headers: {
    "client-token": CLIENT_TOKEN,
  },
});

const Calling: FunctionComponent<{ type: "reception" | "employee" }> =
  React.memo(({ type }) => {
    const navigate = useNavigate();
    const { online } = useOfflineContext();

    const {
      location: { pathname },
    } = window;

    const path = useMemo(() => {
      if (pathname.includes("delivery")) {
        return `${
          pathname.includes("custom") ? "custom-" : ""
        }delivery/:action/contact/:id/:callType`;
      }

      if (pathname.includes("access")) {
        return `access/checkin/${
          pathname.includes("manual") ? "manual/" : ""
        }contact/:id/:callType`;
      }

      return "contact/:id/:callType";
    }, []);

    const match = useMatch({
      path,
    });

    const id = match?.params?.id;

    const callType = match?.params?.callType;

    const receptionistPhone = useReceptionistPhone();

    const { data: accessEventData } = useQuery(
      [`access/event/${id}`],
      () =>
        client.request<{ accessEvent: IAccessEvent }>(QUERY_ACCESS_EVENT, {
          id,
        }),
      {
        enabled: !!id && id !== "reception",
        retry: false,
        staleTime: 1 * (60 * 1000),
        onError: () => {
          navigate("/", { replace: true });
        },
      },
    );

    const receiverPhone =
      accessEventData?.accessEvent?.receiver?.phone ||
      accessEventData?.accessEvent?.customReceiver?.phone;

    const phoneNumber = useMemo(() => {
      if (type === "reception") {
        return receptionistPhone;
      }

      return receiverPhone;
    }, [receptionistPhone, receiverPhone, type]);

    const timerRef = useRef<NodeJS.Timeout>();

    const call = useRef<Call>();
    const device = useRef<Device>();

    const [callingStatus, setCallingStatus] = useState<CallStatus>();

    const { data } = useQuery(
      ["token"],
      () =>
        api
          .get(`/twilio/token`)
          .then(({ data }) => data)
          .catch((error) => {
            Logger.error(`[device] - disconnect`, error);
          }),
      {
        refetchOnWindowFocus: false,
        cacheTime: 0,
        staleTime: 0,
      },
    );

    const onEndCall = useCallback(() => {
      device.current?.disconnectAll();
      device.current?.destroy();
    }, []);

    const onClose = useCallback(() => {
      setCallingStatus(undefined);

      navigate("/", { replace: true });
    }, []);

    useEffect(() => {
      const init = async () => {
        if (!phoneNumber) {
          navigate("/", { replace: true });

          return;
        }

        if (!(data && data.token) || !online) {
          return;
        }

        device.current = new Device(data.token, {
          // debug: true,
          // fakeLocalDTMF: true,
          // enableRingingState: true,
        });

        Logger.debug(`[device] Device connected`);
        Logger.debug(`[call] Establishing a call`);

        setCallingStatus("connecting");

        let params: Record<string, string> = {
          To: phoneNumber,
        };

        if (callType === "ping") {
          params = {
            ...params,
            Timeout: "10",
          };
        }

        call.current = await device.current.connect({
          params,
        });

        call.current.on("ringing", (call) => {
          if (call) {
            setCallingStatus("ringing");
          }
        });

        call.current.on("accept", () => {
          /**
           * If someone answers the "ping" call by chance,
           * end the call automatically
           */
          if (callType === "ping") {
            onEndCall();

            return;
          }

          setCallingStatus("open");
        });

        call.current.on("cancel", () => {
          Logger.debug(`[call] The call has been canceled.`);

          setCallingStatus("close");
        });

        call.current.on("disconnect", (call) => {
          Logger.debug(`[call] The call has been disconnected.`, call);

          setCallingStatus("close");
        });

        call.current.on("reject", () => {
          Logger.debug(`[call] The call was rejected.`);

          setCallingStatus("close");
        });

        call.current.on("error", (error) => {
          Logger.error(`[call] An error has occurred: `, error);

          // trackEvent({ type: "calling", value: "error" });
          setCallingStatus("error");
        });

        call.current.on("reconnected", () => {
          Logger.debug(`[call] The call has regained connectivity.`);
        });

        call.current.on("reconnecting", (error) => {
          Logger.debug(`[call] Connectivity error: `, error);
        });

        device.current.on("error", (twilioError) => {
          Logger.error(`[device] An error has occurred: `, twilioError);

          // trackEvent({ type: "calling", value: "error" });
          setCallingStatus("error");
        });
      };

      init();

      return () => {
        if (!device.current) {
          return;
        }

        device.current.disconnectAll();
        device.current.destroy();
        Logger.debug(`[device] Device disconnected`);
      };
    }, [data, phoneNumber]);

    useEffect(() => {
      if (callingStatus === "close" || callingStatus === "error") {
        timerRef.current = setTimeout(() => {
          return navigate("/", { replace: true });
        }, 5000);
      }

      return () => {
        clearTimeout(timerRef.current);
      };
    }, [callingStatus]);

    return (
      <CallingModal
        status={callingStatus}
        isCallingReception={type === "reception"}
        onEndCall={onEndCall}
        onClose={onClose}
      />
    );
  });

export default Calling;
